import { OwnershipLabelsProvider } from "../kyc/OwnershipLabels";
import KycInitialDocumentsModal from "../kyc/sections/client/KycInitialDocumentsModal";
import { documentsRequestConfig, entityDocumentsTitle } from "../utils/documentsRequest";
import { clientEntityTypeOf, entityTypeComplete } from "../kyc/clientEntityType";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Toast } from "primereact/toast";
import { signInWithCustomToken, signOut } from "firebase/auth";
import { ref as storageRef, uploadBytesResumable } from "firebase/storage";

import { appConfirm } from "../components/ui/appConfirm";
import { publicAuth as embeddedDocAuth, publicStorage as embeddedDocStorage } from "../firebase";
import KycMobileCustomerDetails from "../kyc/sections/mobile/KycMobileCustomerDetails";
import KycMobileOwnership from "../kyc/sections/mobile/KycMobileOwnership";
import KycMobileSowSof from "../kyc/sections/mobile/KycMobileSowSof";
import KycMobilePepSanctions from "../kyc/sections/mobile/KycMobilePepSanctions";
import { MOBILE_TEXT_ENTRY_PROPS } from "../kyc/sections/mobile/mobileTextEntryProps";
import { buildLiveReviewOwnershipSnapshot } from "../kyc/sections/review/KycReviewOwnershipSection";
import logoMyKycGrey from "../images/mykyc-logo-grey-1000.png";
import logoMyKycLightblue3 from "../images/mykyc-logo-blue-800px.png";
import avatarIcon from "../images/avatar.svg";
import businessTradeIcon from "../images/businessTrade.svg";
import roiIcon from "../images/roi.svg";
import Lottie from "lottie-react";
import timerExpAnim from "../images/timer-exp-b.json";
import {
  buildClientKycSaveData,
  toKycDateOrNull,
} from "../utils/clientKycPayload";
import {
  buildEmbeddedKycDocumentRequirements,
  embeddedMakeId,
  embeddedSafeStr,
  formatEmbeddedBytes,
  sanitizeEmbeddedFilename,
} from "../utils/embeddedKycDocuments";
import { KYC_SUBMISSION_API_BASE } from "../config/api";

import "./css/KycClientSideFormPage.css";
import "./css/KycClientSideFormMobilePage.css";
import "./css/ClientDocumentsUploadPage.css";

const PRE_LANDING_INTRO = "Secure onboarding, simplified";
const PRE_LANDING_INTRO_WORDS = ["Secure", "onboarding,", "simplified"];
const pageAnim = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
};

const steadyPageAnim = {
  initial: { opacity: 1, y: 0 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 1, y: 0 },
};

const introContentFadeAnim = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.36,
      delay: 0.14,
      ease: "easeOut",
    },
  },
};

const stepBodyFadeAnim = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: [0.22, 1, 0.36, 1],
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.18,
      ease: [0.4, 0, 1, 1],
    },
  },
};

const API_BASE = KYC_SUBMISSION_API_BASE;
const MOBILE_SUBMIT_PREVIEW_MODE = false;
const PRE_LANDING_MS = 5900;

function iso2ToFlagEmoji(iso2) {
  const cc = String(iso2 || "").trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return "";
  const A = 0x1f1e6;
  return String.fromCodePoint(
    A + (cc.charCodeAt(0) - 65),
    A + (cc.charCodeAt(1) - 65)
  );
}

function getLineFlag({ req, line }) {
  const kind = req?.meta?.kind;
  const text = String(line || "").toLowerCase();
  if (kind === "individual") {
    if (text.includes("passport")) return iso2ToFlagEmoji(req?.meta?.nationalityIso2);
    if (
      text.includes("proof of address") ||
      text.includes("proof of adress") ||
      text.includes("utility bill") ||
      text.includes("poa")
    ) {
      return iso2ToFlagEmoji(req?.meta?.domicileIso2);
    }
  }
  return "";
}

function stripMetaInTitle(title) {
  return String(title || "")
    .replace(/\s*\(Nationality:\s*[A-Za-z]{2}\)\s*/g, " ")
    .replace(/\s*\(Domicile:\s*[A-Za-z]{2}\)\s*/g, " ")
    .replace(/\s*\(Country:\s*[A-Za-z]{2}\)\s*/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function parseRichLines(desc) {
  const raw = String(desc || "").trim();
  if (!raw) return [];
  return raw
    .split("\n")
    .map((x) => x.trim())
    .filter(Boolean)
    .map((line) => {
      const text = line.startsWith("•") ? line.slice(1).trim() : line;
      const lower = text.toLowerCase();
      const isHeading =
        lower === "required:" ||
        lower.startsWith("provide at least one of:") ||
        /^provide at least \d+ of:/.test(lower) ||
        lower.startsWith("also provide (optional):");
      const isNote = /^\(.+\)$/.test(text);
      return { type: isHeading ? "heading" : isNote ? "note" : "bullet", text };
    });
}

function isConditionalEntityRequirement(req, lines) {
  if (req?.meta?.kind !== "entity") return false;
  const hasConditionNote = (lines || []).some(
    (line) => line.type === "note" && /depend|applicable|mainland|free zone/i.test(String(line.text || ""))
  );
  const bulletCount = (lines || []).filter((line) => line.type === "bullet").length;
  return hasConditionNote && bulletCount > 1;
}

function displayRequirementHeading(req, lines, headingText, headingIndex) {
  const text = String(headingText || "");
  if (text.toLowerCase() !== "required:") return text;

  if (isConditionalEntityRequirement(req, lines)) return "Provide at least 1 of:";

  let count = 0;
  for (let i = headingIndex + 1; i < lines.length; i++) {
    if (lines[i].type === "heading") break;
    if (lines[i].type === "bullet") count++;
  }
  return count === 1 ? "Provide the following:" : "Provide all of the following:";
}

function requirementLinesForDisplay(req, lines) {
  const indexedLines = (lines || []).map((line, index) => ({ ...line, _displayIndex: index }));
  if (!isConditionalEntityRequirement(req, indexedLines)) return indexedLines;
  return [
    ...indexedLines.filter((line) => line.type === "heading"),
    ...indexedLines.filter((line) => line.type === "bullet"),
    ...indexedLines.filter((line) => line.type === "note"),
  ];
}

function renderRequirementNoteText(req, lines, text) {
  const value = String(text || "").trim();
  if (!isConditionalEntityRequirement(req, lines)) return value;
  const unwrapped = value.replace(/^\((.*)\)$/s, "$1").trim();
  return `Note: ${unwrapped}`;
}

function getPartyIcon(kind) {
  if (kind === "entity") return businessTradeIcon;
  if (kind === "individual") return avatarIcon;
  return roiIcon;
}

function getPartyPill(kind) {
  if (kind === "entity") return "Entity";
  if (kind === "individual") return "Individual";
  return "";
}
const COMMENT_MIN_HEIGHT = "calc(1.45em * 3 + 1.1rem)";
const COMMENT_MAX_HEIGHT = "calc(1.45em * 20 + 1.1rem)";
const MOBILE_SOW_OPTION_VALUES = new Set([
  "employment",
  "self_employed",
  "business_owner",
  "sale_business",
  "sale_real_estate",
  "rental_income",
  "investment_returns",
  "inheritance",
  "gift",
  "legal_settlement",
  "divorce_settlement",
  "virtual_asset_gains",
  "other",
]);
const MOBILE_SOF_OPTION_VALUES = new Set([
  "employment_income",
  "professional_income",
  "business_income",
  "sale_business_proceeds",
  "sale_property_proceeds",
  "rental_income_sof",
  "sale_securities",
  "dividends_distribution",
  "inheritance_proceeds",
  "gift_proceeds",
  "legal_settlement_proceeds",
  "divorce_settlement_proceeds",
  "sale_virtual_assets",
  "loan",
  "other",
]);
const MOBILE_PAYMENT_ROUTES_OPTION_VALUES = new Set([
  "transfer_own_account",
  "third_party_payment",
  "cash_deposit",
  "cheque",
  "card_payment",
  "virtual_assets_transfer",
  "other",
]);

function autoResizeTextarea(el, { minLines = 3, maxLines = 10 } = {}) {
  if (!el) return;
  const computed = window.getComputedStyle(el);
  const lineHeight = parseFloat(computed.lineHeight) || 24;
  const paddingTop = parseFloat(computed.paddingTop) || 0;
  const paddingBottom = parseFloat(computed.paddingBottom) || 0;
  const borderTop = parseFloat(computed.borderTopWidth) || 0;
  const borderBottom = parseFloat(computed.borderBottomWidth) || 0;
  const verticalExtras = paddingTop + paddingBottom + borderTop + borderBottom;
  const minHeight = lineHeight * minLines + verticalExtras;
  const maxHeight = lineHeight * maxLines + verticalExtras;

  el.style.height = "auto";
  const contentHeight = el.scrollHeight;
  const nextHeight = Math.min(Math.max(contentHeight, minHeight), maxHeight);
  el.style.height = `${nextHeight}px`;
  el.style.overflowY = contentHeight > maxHeight ? "auto" : "hidden";
}

function lsKey(id, token, suffix) {
  const t = encodeURIComponent(token || "");
  return `kyc:${id}:${t}:${suffix}`;
}

async function fetchJsonSafe(url, options) {
  const resp = await fetch(url, options);
  let rawText = "";
  let json = {};

  try {
    rawText = await resp.text();
    json = rawText ? JSON.parse(rawText) : {};
  } catch {}

  return { resp, json, rawText };
}

function formatSubmittedAt(value) {
  if (!value || value === "Already submitted") return "";

  let dateValue = null;
  if (value instanceof Date) {
    dateValue = value;
  } else if (typeof value?.toDate === "function") {
    dateValue = value.toDate();
  } else if (typeof value === "object" && value.seconds) {
    dateValue = new Date(Number(value.seconds) * 1000);
  } else {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) dateValue = parsed;
  }

  if (!dateValue || Number.isNaN(dateValue.getTime())) return "";
  return dateValue.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function firstSubmittedAtText(...sources) {
  for (const source of sources) {
    const formatted = formatSubmittedAt(source);
    if (formatted) return formatted;
  }
  return "";
}

function isValidEmail(value) {
  const v = String(value || "").trim();
  if (!v) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

function isValidWebsite(value) {
  const v = String(value || "").trim();
  if (!v) return false;
  try {
    const normalized = /^https?:\/\//i.test(v) ? v : `https://${v}`;
    const url = new URL(normalized);
    return Boolean(url.hostname && url.hostname.includes("."));
  } catch {
    return false;
  }
}

function normalizePepSanctionsForMobile(value) {
  const raw = value || {};
  const hasPepSelection = typeof raw.pepDeclared === "boolean";
  const hasSanctionsSelection = typeof raw.sanctionsDeclared === "boolean";

  return {
    ...raw,
    pepDeclared: hasPepSelection ? raw.pepDeclared : null,
    sanctionsDeclared: hasSanctionsSelection ? raw.sanctionsDeclared : null,
  };
}

function hasRealDate(value) {
  return !!toKycDateOrNull(value);
}

function normalizeSelectedOptions(list, validSet) {
  if (!Array.isArray(list)) return [];
  return list.filter((value) => validSet.has(String(value || "").trim()));
}

function normalizeOwnershipModeToken(value) {
  const raw = String(value || "").trim();
  const key = raw.toLowerCase();
  if (!raw) return "";
  if (key === "pic" || key.includes("domiciliary") || key.includes("personal investment")) {
    return "pic";
  }
  if (key === "holdingactive" || key === "active holding" || key.includes("holding (active)")) {
    return "holdingActive";
  }
  if (key === "holdingpassive" || key === "passive holding" || key.includes("holding (passive)")) {
    return "holdingPassive";
  }
  if (key === "quoted" || key === "quoted company") return "quoted";
  if (key === "trust") return "trust";
  if (key === "foundation") return "foundation";
  if (
    key === "noneofabove" ||
    key === "operating" ||
    key === "operational" ||
    key === "operating company" ||
    key === "non quoted operating company" ||
    key === "non quoted operational company"
  ) {
    return "noneOfAbove";
  }
  return "";
}

function deriveOwnershipStatus(ownership = {}) {
  const flags = ownership?.uboFlags || {};
  const desktopEntityType = String(ownership?.desktopClientEntityType || "")
    .trim()
    .toLowerCase();
  const desktopHoldingKind = String(ownership?.desktopClientHoldingKind || "")
    .trim()
    .toLowerCase();
  const mobileDraftMode = normalizeOwnershipModeToken(
    ownership?.mobileAssistantDraft?.preliminaryMode
  );
  const snapshotMode = normalizeOwnershipModeToken(ownership?.assistantTree?.initialAHType);
  const hasRows =
    (Array.isArray(ownership?.shareholders) && ownership.shareholders.length > 0) ||
    (Array.isArray(ownership?.ubos) && ownership.ubos.length > 0);

  let mode = "";
  if (flags.accountHolderTrust) mode = "trust";
  else if (flags.accountHolderFoundation) mode = "foundation";
  else if (flags.quotedCompany) mode = "quoted";
  else if (flags.pctNotMeaningful) mode = "pic";
  else if (ownership?.preliminaryNoneOfAbove) mode = "noneOfAbove";
  else if (desktopEntityType === "holding" && desktopHoldingKind === "passive") mode = "holdingPassive";
  else if (desktopEntityType === "holding" && desktopHoldingKind === "active") mode = "holdingActive";
  else if (desktopEntityType) mode = normalizeOwnershipModeToken(desktopEntityType);
  else if (mobileDraftMode) mode = mobileDraftMode;
  else if (snapshotMode) mode = snapshotMode;
  else if (hasRows || ownership?.assistantTree?.mermaid || ownership?.assistantGenerated) {
    mode = "noneOfAbove";
  }

  return {
    mode,
    isQuotedCompany: mode === "quoted",
    isPicOrPassiveHolding: mode === "pic" || mode === "holdingPassive",
    isFoundationAccountHolder: mode === "foundation",
    isTrustAccountHolder: mode === "trust",
    isNoneOfAbove: mode === "noneOfAbove" || mode === "holdingActive",
    hasPreliminarySelection: !!mode,
  };
}

function sanitizeMobilePercent(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits === "10000") return "10000";
  return digits.slice(0, 4);
}

function normalizeMobilePercentForSave(value) {
  const cleaned = sanitizeMobilePercent(value);
  if (!cleaned) return "";
  if (cleaned === "10000") return "100.00";
  const padded = cleaned.padEnd(4, "0");
  return `${padded.slice(0, 2)}.${padded.slice(2, 4)}`;
}

function normalizeMobilePercentForReviewSnapshot(value) {
  if (value === "" || value == null) return value;
  const normalized = normalizeMobilePercentForSave(value);
  if (!normalized) return value;
  const n = Number(normalized);
  if (!Number.isFinite(n)) return value;
  return Number.isInteger(n) ? String(n) : normalized;
}

function normalizeMobileOwnershipPercentsForReviewSnapshot(ownership = {}) {
  const normalizeRow = (row) => {
    if (!row || typeof row !== "object") return row;
    return {
      ...row,
      ownershipPercent: normalizeMobilePercentForReviewSnapshot(row.ownershipPercent),
      effectiveOwnershipPercent: normalizeMobilePercentForReviewSnapshot(
        row.effectiveOwnershipPercent
      ),
    };
  };

  return {
    ...ownership,
    shareholders: Array.isArray(ownership?.shareholders)
      ? ownership.shareholders.map(normalizeRow)
      : ownership?.shareholders,
    directShareholders: Array.isArray(ownership?.directShareholders)
      ? ownership.directShareholders.map(normalizeRow)
      : ownership?.directShareholders,
    ubos: Array.isArray(ownership?.ubos)
      ? ownership.ubos.map(normalizeRow)
      : ownership?.ubos,
    controllingPersonsNatural: Array.isArray(ownership?.controllingPersonsNatural)
      ? ownership.controllingPersonsNatural.map(normalizeRow)
      : ownership?.controllingPersonsNatural,
  };
}

function stepCopy(stepId, clientName, isEntity = false, entityTitle = "Company Registration Documents") {
  if (stepId === "intro") {
    return {
      eyebrow: "Welcome",
      title: "Let’s complete this KYC form",
      hint: "We’ll guide you through a few short steps.",
    };
  }
  if (stepId === "customer") {
    return {
      eyebrow: "Step",
      title: "Customer profile",
      hint: "Start with identity, address, and key background details.",
    };
  }
  if (stepId === "ownership") {
    return {
      eyebrow: "Step",
      title: "Ownership & control",
      hint: "Tell us who owns and controls the entity.",
    };
  }
  if (stepId === "signatories") {
    return {
      eyebrow: "Step",
      title: "Authorised signatories",
      hint: "Please list all individuals or entities authorised to legally bind the company, including those authorised to operate bank accounts or sign contracts.",
    };
  }
  if (stepId === "wealth") {
    return {
      eyebrow: "Step",
      title: "Source of wealth & funds",
      hint: "Help us understand wealth profile and funding sources.",
    };
  }
  if (stepId === "pepSanctions") {
    return {
      eyebrow: "Step",
      title: "PEP and sanctions declaration",
      hint: "Declare any politically exposed person or sanctions exposure.",
    };
  }
  if (stepId === "documents") {
    return {
      eyebrow: "Step",
      title: isEntity ? entityTitle : "Identification Documents",
      hint: isEntity
        ? `Upload the company's ${entityTitle.replace(/^Company /, "").toLowerCase()} now, or choose to provide them later.`
        : "Upload identification documents now, or choose to provide them later.",
    };
  }
  return {
    eyebrow: "Final step",
    title: "Comments (optional)",
    hint: "Add any final clarifications if needed, save your draft, or submit the completed form.",
  };
}

export default function KycClientSideFormMobilePage() {
  const reduceIntakeMotion = useReducedMotion();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const toastRef = useRef(null);
  const topRef = useRef(null);
  const commentTextareaRef = useRef(null);
  const embeddedDocFileInputRef = useRef(null);
  const embeddedAutoUploadRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadErr, setLoadErr] = useState("");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [separateNames, setSeparateNames] = useState(false);
  const [orgJurisdiction, setOrgJurisdiction] = useState(null);
  const [clientName, setClientName] = useState("");
  const [clientType, setClientType] = useState("");
  const [intakeFirstCompleted, setIntakeFirstCompleted] = useState(false);
  // Org setting: documents uploaded at the start / at the end of the form, for individuals or entities.
  const [documentsRequest, setDocumentsRequest] = useState(() => documentsRequestConfig(null));
  const [corporateDocuments, setCorporateDocuments] = useState(null);
  const [useMainCountryOfOperation, setUseMainCountryOfOperation] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedAtText, setSubmittedAtText] = useState("");
  const [stepIndex, setStepIndex] = useState(0);
  const [showPreLanding, setShowPreLanding] = useState(true);
  const [pepSanctionsContinueAttempted, setPepSanctionsContinueAttempted] =
    useState(false);
  const [customerContinueAttempted, setCustomerContinueAttempted] =
    useState(false);
  const [wealthContinueAttempted, setWealthContinueAttempted] = useState(false);
  const [ownershipContinueAttempted, setOwnershipContinueAttempted] =
    useState(false);
  const [toastBackdropVisible, setToastBackdropVisible] = useState(false);
  const [embeddedDocChoice, setEmbeddedDocChoice] = useState("");
  const [embeddedDocSession, setEmbeddedDocSession] = useState(null);
  const [embeddedDocItems, setEmbeddedDocItems] = useState([]);
  const [embeddedDocActiveCardId, setEmbeddedDocActiveCardId] = useState("");
  const [embeddedDocBusy, setEmbeddedDocBusy] = useState(false);
  const [embeddedDocAttempted, setEmbeddedDocAttempted] = useState(false);
  const [embeddedDocError, setEmbeddedDocError] = useState("");

  const [form, setForm] = useState({
    engagement: {
      clientRequests: [],
      approach: {
        channel: "Direct",
        introducerName: "",
        paidIntroducer: false,
      },
    },
    customer: {
      type: "individual",
      name: "",
      nationalityOrIncorporationCountry: "",
      mainCountryOfOperation: "",
      dobOrIncorporationDate: null,
      email: "",
      phone: "",
      address: "",
      city: "",
      country: "",
      website: "",
      businessActivity: "",
    },
    uaeCompany: {
      proposedEntityNames: "",
      jurisdiction: "Mainland",
      activities: "",
      expectedMonthlyTurnoverRange: "",
      expectedCustomerCountries: [],
      expectedSupplierCountries: [],
      expectedTransactionChannels: [],
      highRiskCountries: [],
    },
    ownership: {
      shareholders: [],
      ubos: [],
      controllers: [],
    },
    sourceOfFundsWealth: {
      sourceOfFunds: "",
      sourceOfFundsDetails: "",
      sourceOfWealth: [],
      sourceOfWealthDetails: "",
      supportingDocuments: [],
      netWorthCurrency: "USD",
      netWorthRange: "",
      annualIncomeCurrency: "USD",
      annualIncomeRange: "",
      annualTurnoverCurrency: "USD",
      annualTurnoverRange: "",
      sourceOfFundsTypes: [],
      sourceOfFundsLoanDetails: "",
      sourceOfFundsOtherDetails: "",
      paymentRoutes: [],
      paymentRoutesThirdPartyDetails: "",
      paymentRoutesOtherDetails: "",
    },
    pepSanctions: {
      pepDeclared: null,
      pepDetails: "",
      sanctionsDeclared: null,
      sanctionsDetails: "",
    },
    documents: {
      individualDocs: [],
      corporateDocs: [],
      missingDocs: [],
      notes: "",
    },
    internalUse: {
      riskRating: "Medium",
      eddRequired: false,
      eddRationale: "",
      screeningSummary: "",
      goAmlFiled: false,
      goAmlReference: "",
    },
    clientComment: "",
  });

  const customerType = form?.customer?.type || "individual";
  const isEntity = customerType === "entity";
  // "none" | "start" | "end" for this client (individual or entity).
  const documentsTiming = isEntity ? documentsRequest.entities : documentsRequest.individuals;
  const shouldShowEmbeddedDocuments = documentsTiming === "end";
  const incorporationCountry = isEntity
    ? String(form?.customer?.nationalityOrIncorporationCountry || "").trim().toUpperCase()
    : "";
  // Entity: the registration documents of its country of incorporation (names shown to the client).
  useEffect(() => {
    if (!isEntity || documentsTiming === "none" || !/^[A-Z]{2}$/.test(incorporationCountry) || !id || !token) {
      setCorporateDocuments(null);
      return undefined;
    }
    let alive = true;
    const url = `${API_BASE}/myKyc/clients-side/kyc/corporate-documents?clientId=${encodeURIComponent(id)}&token=${encodeURIComponent(token)}&country=${encodeURIComponent(incorporationCountry)}`;
    // Unavailable list: general guidance only (fallback), never a blocked upload.
    const unavailable = { country: incorporationCountry, fallback: true, unavailable: true, cases: [] };
    fetchJsonSafe(url)
      .then(({ resp, json }) => { if (alive) setCorporateDocuments(resp.ok && json?.ok ? json : unavailable); })
      .catch(() => { if (alive) setCorporateDocuments(unavailable); });
    return () => { alive = false; };
  }, [isEntity, documentsTiming, incorporationCountry, id, token]);
  const embeddedDocRequirements = useMemo(
    () =>
      shouldShowEmbeddedDocuments
        ? buildEmbeddedKycDocumentRequirements(form, clientName || "Client", undefined, corporateDocuments)
        : [],
    [form, clientName, shouldShowEmbeddedDocuments, corporateDocuments]
  );
  const shouldRenderEmbeddedDocuments =
    shouldShowEmbeddedDocuments && embeddedDocRequirements.length > 0;
  const sectionNo = useMemo(() => {
    let n = 1;
    const out = {};
    out.customerDetails = n++;
    out.ownership = isEntity ? n++ : null;
    out.signatories = isEntity ? n++ : null;
    out.sowSof = n++;
    out.sanctionsPep = n++;
    return out;
  }, [isEntity]);

  const hasInitialDocumentsStep = documentsTiming === "start";
  // Company: the client entity type is answered in the upload-first step, not in ownership.
  const entityTypeAnsweredEarlier = isEntity && hasInitialDocumentsStep && entityTypeComplete(clientEntityTypeOf(form?.ownership));
  const steps = useMemo(() => {
    const out = [
      { id: "intro", label: "Intro" },
      ...(hasInitialDocumentsStep ? [{ id: "initialDocuments", label: "Upload documents" }] : []),
      { id: "customer", label: "Customer" },
    ];
    if (isEntity) {
      out.push({ id: "ownership", label: "Ownership" });
      out.push({ id: "signatories", label: "Signatories" });
    }
    out.push(
      { id: "wealth", label: "SoW / SoF" },
      { id: "pepSanctions", label: "PEP and sanctions declaration" }
    );
    if (shouldRenderEmbeddedDocuments) {
      out.push({ id: "documents", label: isEntity ? entityDocumentsTitle(documentsRequest) : "Identification Documents" });
    }
    out.push(
      { id: "comments", label: "Comments (optional)" }
    );
    return out;
  }, [isEntity, shouldRenderEmbeddedDocuments, hasInitialDocumentsStep, documentsRequest]);

  const activeStep = steps[stepIndex] || steps[0];
  const progressSteps = steps.filter((step) => step.id !== "intro");
  const progressStepIndex = Math.max(
    0,
    progressSteps.findIndex((step) => step.id === activeStep?.id)
  );
  const embeddedDoneCardIds = useMemo(
    () =>
      new Set(
        embeddedDocItems
          .filter((item) => item.status === "done" && item.objectPath)
          .map((item) => embeddedSafeStr(item.cardId))
      ),
    [embeddedDocItems]
  );
  const embeddedGroupedRequirements = useMemo(() => {
    const map = new Map();
    for (const req of embeddedDocRequirements || []) {
      const kind = embeddedSafeStr(req?.meta?.kind);
      const partyName = embeddedSafeStr(req?.meta?.partyName);
      const partyRole = embeddedSafeStr(req?.meta?.partyRole);
      const groupKey =
        kind === "individual" || kind === "entity"
          ? `party::${kind}::${
              embeddedSafeStr(req?.meta?.partyId) ||
              partyName ||
              embeddedSafeStr(req?.cardId)
            }`
          : `other::${kind || "misc"}::${
              embeddedSafeStr(req?.meta?.sowKey) || embeddedSafeStr(req?.cardId)
            }`;
      if (!map.has(groupKey)) {
        map.set(groupKey, { key: groupKey, kind, partyName, partyRole, requirements: [] });
      }
      map.get(groupKey).requirements.push(req);
    }
    return Array.from(map.values());
  }, [embeddedDocRequirements]);
  const embeddedQueuedCount = useMemo(
    () => embeddedDocItems.filter((item) => item.status === "queued").length,
    [embeddedDocItems]
  );
  const isEmbeddedDocumentsStepComplete =
    !shouldRenderEmbeddedDocuments ||
    embeddedDocChoice === "later" ||
    (embeddedDocChoice === "upload_now" &&
      embeddedQueuedCount === 0 &&
      embeddedDocRequirements.every((req) =>
        embeddedDoneCardIds.has(embeddedSafeStr(req.cardId))
      ));
  const showProgressHeader = activeStep?.id !== "intro";
  const customer = form?.customer || {};
  const ownershipData = form?.ownership || {};
  const derivedOwnershipStatus = deriveOwnershipStatus(ownershipData);
  const sowSof = form?.sourceOfFundsWealth || {};
  const pepSanctions = form?.pepSanctions || {};
  const isQuotedCompany = derivedOwnershipStatus.isQuotedCompany;
  const isPicOrPassiveHolding = derivedOwnershipStatus.isPicOrPassiveHolding;
  const isFoundationAccountHolder = derivedOwnershipStatus.isFoundationAccountHolder;
  const isTrustAccountHolder = derivedOwnershipStatus.isTrustAccountHolder;
  const isTrustOrFoundationAccountHolder =
    isTrustAccountHolder || isFoundationAccountHolder;
  const accountHolderTrustNameKind = String(
    ownershipData?.accountHolderTrustNameKind || ""
  )
    .trim()
    .toLowerCase();
  const hasTrustNameKindSelection =
    !isTrustAccountHolder ||
    accountHolderTrustNameKind === "trust" ||
    accountHolderTrustNameKind === "trustee";
  const hasOwnershipRows =
    (Array.isArray(ownershipData?.shareholders) &&
      ownershipData.shareholders.length > 0) ||
    (Array.isArray(ownershipData?.ubos) && ownershipData.ubos.length > 0) ||
    (Array.isArray(ownershipData?.signatories) &&
      ownershipData.signatories.length > 0);
  const legalArrangements = Array.isArray(ownershipData?.legalArrangements?.arrangements)
    ? ownershipData.legalArrangements.arrangements
    : [];
  const normalizeOwnershipHolderType = (value) => {
    const raw = String(value || "").trim().toLowerCase();
    if (raw === "entity") return "entity";
    if (raw === "trust") return "trust";
    if (raw === "foundation") return "foundation";
    return "individual";
  };
  const normalizeArrangementType = (value) =>
    String(value || "").trim().toLowerCase() === "foundation"
      ? "Foundation"
      : "Trust";
  const isFilled = (value) => String(value || "").trim().length > 0;
  const hasOwnershipPercent = (value) => isFilled(value);
  const isCorporateKind = (value) => {
    const raw = String(value || "").trim().toLowerCase();
    return raw === "entity" || raw === "company" || raw === "corporate";
  };
  const isCorporateArrangementParty = (party) => {
    const role = String(party?.role || "").trim().toLowerCase();
    const kind = party?.trusteeKind || party?.councilKind;
    return (
      isCorporateKind(kind) &&
      (role === "trustee" ||
        role === "protector" ||
        role === "council member" ||
        role === "guardian")
    );
  };
  const corporateArrangementPartyMatchesRow = (row, party) => {
    const rowName = String(row?.name || "").trim().toLowerCase();
    const partyName = String(party?.name || "").trim().toLowerCase();
    if (!rowName || rowName !== partyName) return false;

    const rowCountry = String(row?.countryOfResidence || "").trim().toLowerCase();
    const partyCountry = String(
      party?.domicile || party?.countryOfIncorporation || ""
    )
      .trim()
      .toLowerCase();
    return !rowCountry || !partyCountry || rowCountry === partyCountry;
  };
  const isCorporateArrangementControlRow = (row) => {
    const basis = String(row?.basisOfControl || "").toLowerCase();
    const basisLooksCorporate =
      (basis.includes("trust") ||
        basis.includes("foundation") ||
        basis.includes("council") ||
        basis.includes("guardian") ||
        basis.includes("protector")) &&
      (basis.includes("corporate") ||
        basis.includes("company") ||
        basis.includes("entity"));
    if (basisLooksCorporate) return true;

    return legalArrangements.some((arrangement) =>
      (Array.isArray(arrangement?.parties) ? arrangement.parties : []).some(
        (party) =>
          isCorporateArrangementParty(party) &&
          corporateArrangementPartyMatchesRow(row, party)
      )
    );
  };
  const isTrustPartyComplete = (party) => {
    const role = String(party?.role || "").trim();
    const trusteeKind = String(party?.trusteeKind || "").trim().toLowerCase();
    if (!isFilled(party?.name)) return false;
    if (role === "Trustee" && isCorporateKind(trusteeKind)) {
      return isFilled(party?.domicile);
    }
    return isFilled(party?.nationality) && isFilled(party?.domicile);
  };
  const isFoundationPartyComplete = (party) => {
    const role = String(party?.role || "").trim();
    const councilKind = String(party?.councilKind || "").trim().toLowerCase();
    if (!isFilled(party?.name)) return false;
    if (role === "Council member") {
      if (!["individual", "entity", "company", "corporate"].includes(councilKind)) return false;
      return isCorporateKind(councilKind)
        ? isFilled(party?.domicile)
        : isFilled(party?.nationality) && isFilled(party?.domicile);
    }
    return isFilled(party?.nationality) && isFilled(party?.domicile);
  };
  const getAccountHolderArrangement = () =>
    legalArrangements.find(
      (arrangement) =>
        String(arrangement?.source || "") === "account_holder" &&
        ((isTrustAccountHolder &&
          normalizeArrangementType(arrangement?.arrangementType) === "Trust") ||
          (isFoundationAccountHolder &&
            normalizeArrangementType(arrangement?.arrangementType) === "Foundation"))
    ) || null;
  const getShareholderArrangement = (shareholder) =>
    legalArrangements.find(
      (arrangement) =>
        String(arrangement?.source || "") === "shareholder" &&
        String(arrangement?.sourceRefRowId || "") ===
          String(shareholder?._rowId || "") &&
        normalizeArrangementType(arrangement?.arrangementType) ===
          (normalizeOwnershipHolderType(shareholder?.holderType) === "foundation"
            ? "Foundation"
            : "Trust")
    ) || null;
  const isTrustHolderComplete = (item, trustNameKind = "") => {
    const parties = Array.isArray(item?.trustParties)
      ? item.trustParties
      : Array.isArray(item?.parties)
      ? item.parties
      : [];
    const trustee = parties.find(
      (party) => String(party?.role || "").trim() === "Trustee"
    );
    const hasIdentity =
      trustNameKind === "trustee"
        ? isFilled(trustee?.name)
        : isFilled(item?.name) || isFilled(trustee?.name);
    return hasIdentity && parties.length > 0 && parties.every(isTrustPartyComplete);
  };
  const isFoundationHolderComplete = (item) => {
    const parties = Array.isArray(item?.foundationParties)
      ? item.foundationParties
      : Array.isArray(item?.parties)
      ? item.parties
      : [];
    return isFilled(item?.name) && parties.length > 0 && parties.every(isFoundationPartyComplete);
  };
  const shareholdersList = Array.isArray(ownershipData?.shareholders)
    ? ownershipData.shareholders
    : [];
  const ubosList = Array.isArray(ownershipData?.ubos) ? ownershipData.ubos : [];
  const signatoriesList = Array.isArray(ownershipData?.signatories)
    ? ownershipData.signatories
    : [];
  const areShareholdersComplete = shareholdersList.every((item) => {
    const holderType = normalizeOwnershipHolderType(item?.holderType);
    if (holderType === "individual") {
      return (
        isFilled(item?.name) &&
        isFilled(item?.nationality) &&
        isFilled(item?.countryOfResidence) &&
        (isPicOrPassiveHolding || hasOwnershipPercent(item?.ownershipPercent))
      );
    }
    if (holderType === "entity") {
      return (
        isFilled(item?.name) &&
        isFilled(item?.countryOfResidence) &&
        (isPicOrPassiveHolding || hasOwnershipPercent(item?.ownershipPercent))
      );
    }
    if (holderType === "trust") {
      return (
        (isPicOrPassiveHolding || hasOwnershipPercent(item?.ownershipPercent)) &&
        isTrustHolderComplete(
          {
            ...(item || {}),
            parties: getShareholderArrangement(item)?.parties,
          },
          ""
        )
      );
    }
    if (holderType === "foundation") {
      return (
        (isPicOrPassiveHolding || hasOwnershipPercent(item?.ownershipPercent)) &&
        isFoundationHolderComplete({
          ...(item || {}),
          parties: getShareholderArrangement(item)?.parties,
        })
      );
    }
    return false;
  });
  const areUbosComplete = ubosList.every((item) => {
    const holderType = normalizeOwnershipHolderType(item?.holderType);
    const hasBasis = isFilled(item?.basisOfControl);
    if (!hasBasis) return false;
    if (holderType === "trust") return isTrustHolderComplete(item, "");
    if (holderType === "foundation") return isFoundationHolderComplete(item);
    return (
      isFilled(item?.name) &&
      isFilled(item?.countryOfResidence) &&
      (isCorporateArrangementControlRow(item) || isFilled(item?.nationality))
    );
  });
  const areSignatoriesComplete = signatoriesList.every(
    (item) =>
      isFilled(item?.name) &&
      isFilled(item?.nationality) &&
      isFilled(item?.countryOfResidence)
  );
  const isAccountHolderArrangementComplete = isTrustAccountHolder
    ? isTrustHolderComplete(
        {
          ...(getAccountHolderArrangement() || {}),
          trustParties: getAccountHolderArrangement()?.parties,
        },
        accountHolderTrustNameKind
      )
    : isFoundationAccountHolder
    ? isFoundationHolderComplete({
        ...(getAccountHolderArrangement() || {}),
        foundationParties: getAccountHolderArrangement()?.parties,
      })
    : true;
  const hasOwnershipPreliminarySelection =
    derivedOwnershipStatus.hasPreliminarySelection;
  const isOwnershipPreliminaryValidated = hasOwnershipPreliminarySelection;
  const isCustomerStepComplete =
    String(customer.name || "").trim() &&
    String(customer.nationalityOrIncorporationCountry || "").trim() &&
    (!isEntity ||
      !useMainCountryOfOperation ||
      String(customer.mainCountryOfOperation || "").trim()) &&
    hasRealDate(customer.dobOrIncorporationDate) &&
    String(customer.address || "").trim() &&
    String(customer.city || "").trim() &&
    String(customer.country || "").trim() &&
    isValidEmail(customer.email || "") &&
    String(customer.phone || "").trim() &&
    String(customer.businessActivity || "").trim();
  const isPepSanctionsStepComplete =
    typeof pepSanctions.pepDeclared === "boolean" &&
    typeof pepSanctions.sanctionsDeclared === "boolean";
  const sourceOfWealth = normalizeSelectedOptions(
    sowSof.sourceOfWealth,
    MOBILE_SOW_OPTION_VALUES
  );
  const sourceOfFundsTypes = normalizeSelectedOptions(
    sowSof.sourceOfFundsTypes,
    MOBILE_SOF_OPTION_VALUES
  );
  const paymentRoutes = normalizeSelectedOptions(
    sowSof.paymentRoutes,
    MOBILE_PAYMENT_ROUTES_OPTION_VALUES
  );
  const hasSourceOfWealthSelection = sourceOfWealth.length > 0;
  const hasSourceOfFundsSelection = sourceOfFundsTypes.length > 0;
  const hasPaymentRoutesSelection = paymentRoutes.length > 0;
  const hasSourceOfWealthOtherText =
    !sourceOfWealth.includes("other") ||
    String(sowSof.sourceOfWealthDetails || "").trim();
  const hasSourceOfFundsLoanText =
    !sourceOfFundsTypes.includes("loan") ||
    String(sowSof.sourceOfFundsLoanDetails || "").trim();
  const hasSourceOfFundsOtherText =
    !sourceOfFundsTypes.includes("other") ||
    String(sowSof.sourceOfFundsOtherDetails || "").trim();
  const hasPaymentRoutesThirdPartyText =
    !paymentRoutes.includes("third_party_payment") ||
    String(sowSof.paymentRoutesThirdPartyDetails || "").trim();
  const hasPaymentRoutesOtherText =
    !paymentRoutes.includes("other") ||
    String(sowSof.paymentRoutesOtherDetails || "").trim();
  const requiresCompanyTurnover =
    isEntity &&
    (String(ownershipData?.desktopClientEntityType || "").trim().toLowerCase() === "operating" ||
      (String(ownershipData?.desktopClientEntityType || "").trim().toLowerCase() === "holding" &&
        String(ownershipData?.desktopClientHoldingKind || "").trim().toLowerCase() === "active") ||
      derivedOwnershipStatus.isNoneOfAbove);
  const isWealthStepComplete =
    hasSourceOfWealthSelection &&
    hasSourceOfFundsSelection &&
    hasPaymentRoutesSelection &&
    hasSourceOfWealthOtherText &&
    hasSourceOfFundsLoanText &&
    hasSourceOfFundsOtherText &&
    hasPaymentRoutesThirdPartyText &&
    hasPaymentRoutesOtherText &&
    String(sowSof.netWorthRange || "").trim() &&
    String(sowSof.annualIncomeRange || "").trim() &&
    (!requiresCompanyTurnover || String(sowSof.annualTurnoverRange || "").trim());
  const isOwnershipStepComplete =
    !isEntity ||
    (isOwnershipPreliminaryValidated &&
      hasTrustNameKindSelection &&
      (isQuotedCompany ||
        (isTrustOrFoundationAccountHolder
          ? isAccountHolderArrangementComplete
          : shareholdersList.length > 0 &&
            areShareholdersComplete &&
            areUbosComplete)));
  const isSignatoriesStepComplete =
    !isEntity || (signatoriesList.length > 0 && areSignatoriesComplete);
  const isContinueDisabled =
    saving ||
    (activeStep?.id === "customer" && !isCustomerStepComplete) ||
    (activeStep?.id === "ownership" && !isOwnershipStepComplete) ||
    (activeStep?.id === "signatories" && !isSignatoriesStepComplete) ||
    (activeStep?.id === "wealth" && !isWealthStepComplete) ||
    (activeStep?.id === "pepSanctions" && !isPepSanctionsStepComplete) ||
    (activeStep?.id === "documents" && !isEmbeddedDocumentsStepComplete);
  const showBlockedDocumentsContinue =
    activeStep?.id === "documents" &&
    !isEmbeddedDocumentsStepComplete &&
    !saving;
  const showBlockedPepContinue =
    activeStep?.id === "pepSanctions" &&
    !isPepSanctionsStepComplete &&
    !saving;

  useEffect(() => {
    if (stepIndex > steps.length - 1) setStepIndex(steps.length - 1);
  }, [stepIndex, steps.length]);

  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [stepIndex]);

  useEffect(() => {
    if (activeStep?.id !== "customer") {
      setCustomerContinueAttempted(false);
    }
    if (activeStep?.id !== "wealth") {
      setWealthContinueAttempted(false);
    }
    if (activeStep?.id !== "ownership") {
      setOwnershipContinueAttempted(false);
    }
    if (activeStep?.id !== "pepSanctions") {
      setPepSanctionsContinueAttempted(false);
    }
  }, [activeStep?.id]);

  useEffect(() => {
    if (isCustomerStepComplete) {
      setCustomerContinueAttempted(false);
    }
  }, [isCustomerStepComplete]);

  useEffect(() => {
    if (isOwnershipStepComplete) {
      setOwnershipContinueAttempted(false);
    }
  }, [isOwnershipStepComplete]);

  useEffect(() => {
    if (isWealthStepComplete) {
      setWealthContinueAttempted(false);
    }
  }, [isWealthStepComplete]);

  useEffect(() => {
    if (isPepSanctionsStepComplete) {
      setPepSanctionsContinueAttempted(false);
    }
  }, [isPepSanctionsStepComplete]);

  useEffect(() => {
    if (loading || activeStep?.id !== "intro") return;
    if (!showPreLanding) return;

    const timer = window.setTimeout(() => {
      setShowPreLanding(false);
    }, PRE_LANDING_MS);

    return () => window.clearTimeout(timer);
  }, [loading, activeStep?.id, showPreLanding]);

  function handlePreLandingWordAnimationEnd() {
    setShowPreLanding((prev) => (prev ? false : prev));
  }

  useEffect(() => {
    if (activeStep?.id !== "comments") return;
    const frame = window.requestAnimationFrame(() => {
      autoResizeTextarea(commentTextareaRef.current, { minLines: 3, maxLines: 20 });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [activeStep?.id, form?.clientComment]);

  function setAt(path, value) {
    setMsg("");
    setForm((prev) => {
      const next = structuredClone(prev);
      let ref = next;
      for (let i = 0; i < path.length - 1; i++) ref = ref[path[i]];
      ref[path[path.length - 1]] = value;
      return next;
    });
  }

  useEffect(() => {
    let alive = true;

    async function run() {
      try {
        setLoadErr("");
        setErr("");
        setMsg("");
        setLoading(true);

        if (!id || !token) {
          setLoadErr(
            "This link is missing required parameters (id/token). Please request a new secure link."
          );
          return;
        }

        const alreadySubmitted =
          !MOBILE_SUBMIT_PREVIEW_MODE &&
          window.localStorage.getItem(lsKey(id, token, "submitted")) === "1";

        if (alreadySubmitted) {
          if (!alive) return;
          setSubmitted(true);
          setSubmittedAtText(
            firstSubmittedAtText(
              window.localStorage.getItem(lsKey(id, token, "submittedAt"))
            )
          );
          setClientName(
            window.localStorage.getItem(lsKey(id, token, "clientName")) || ""
          );
          setClientType(
            window.localStorage.getItem(lsKey(id, token, "clientType")) || ""
          );
          return;
        }

        const verifyUrl = `${API_BASE}/myKyc/clients-side/kyc/verify-link?clientId=${encodeURIComponent(
          id
        )}&token=${encodeURIComponent(token)}`;
        const { resp: vResp, json: vj, rawText: vRaw } = await fetchJsonSafe(
          verifyUrl
        );

        if (!vResp.ok || !vj.ok) {
          setLoadErr(
            vj?.error ||
              vRaw ||
              "Link invalid or expired. Please request a new link."
          );
          return;
        }

        if (vj.used) {
          if (!alive) return;
          const headerUrl = `${API_BASE}/myKyc/clients-side/kyc/client-header?clientId=${encodeURIComponent(
            id
          )}&token=${encodeURIComponent(token)}`;
          const { resp: hResp, json: hj } = await fetchJsonSafe(headerUrl);
          if (hResp.ok && hj.ok) {
            setOrgJurisdiction(hj.orgGeo ?? null);
            setClientName(hj.clientName || "");
            setSeparateNames(hj.nameEntryMode === "separate");
            setClientType(hj.clientTypeValue || hj.clientType || "");
          }
          setSubmitted(true);
          setSubmittedAtText(
            firstSubmittedAtText(
              vj.submittedAt,
              hj?.submittedAt
            )
          );
          return;
        }

        const headerUrl = `${API_BASE}/myKyc/clients-side/kyc/client-header?clientId=${encodeURIComponent(
          id
        )}&token=${encodeURIComponent(token)}`;
        const { resp: hResp, json: hj, rawText: hRaw } = await fetchJsonSafe(
          headerUrl
        );

        if (!hResp.ok || !hj.ok) {
          setLoadErr(hj?.error || hRaw || "Failed to load client information.");
          return;
        }

        if (!alive) return;

        setOrgJurisdiction(hj.orgGeo ?? null);
        setClientName(hj.clientName || "");
        setSeparateNames(hj.nameEntryMode === "separate");
        setClientType(hj.clientTypeValue || hj.clientType || "");
        setDocumentsRequest(hj.documentsRequest || documentsRequestConfig({ documentsRequest: { mode: hj.documentsRequestMode } }));
        setUseMainCountryOfOperation(
          hj.useMainCountryOfOperation === true ||
            hj.countryRisk?.useMainCountryOfOperation === true
        );

        const headerType = String(
          hj.clientTypeValue || hj.clientType || ""
        ).toLowerCase();
        const lockedType = headerType === "entity" ? "entity" : "individual";

        setForm((prev) => ({
          ...prev,
          customer: {
            ...prev.customer,
            type: lockedType,
            email: hj.contactEmail || prev.customer.email,
            phone: hj.contactPhone || prev.customer.phone,
          },
        }));

        const draftUrl = `${API_BASE}/myKyc/clients-side/kyc/draft?clientId=${encodeURIComponent(
          id
        )}&token=${encodeURIComponent(token)}`;
        const { resp: dResp, json: dj, rawText: dRaw } = await fetchJsonSafe(
          draftUrl
        );

        if (!dResp.ok || !dj.ok) {
          setLoadErr(dj?.error || dRaw || "Failed to load your saved draft.");
          return;
        }

        if (!alive) return;

        setIntakeFirstCompleted(dj.intakeFirstCompleted === true);
        if (dj.data) {
          const data = dj.data;
          const loadedCustomer = data.customer || {};
          setForm((prev) => ({
            ...prev,
            ...data,
            engagement: { ...prev.engagement, ...(data.engagement || {}) },
            customer: {
              ...prev.customer,
              ...loadedCustomer,
              type: lockedType,
              dobOrIncorporationDate: toKycDateOrNull(
                loadedCustomer.dobOrIncorporationDate
              ),
            },
            uaeCompany: { ...prev.uaeCompany, ...(data.uaeCompany || {}) },
            ownership: { ...prev.ownership, ...(data.ownership || {}) },
            sourceOfFundsWealth: {
              ...prev.sourceOfFundsWealth,
              ...(data.sourceOfFundsWealth || {}),
              sourceOfWealth: Array.isArray(
                data.sourceOfFundsWealth?.sourceOfWealth
              )
                ? data.sourceOfFundsWealth.sourceOfWealth
                : [],
              sourceOfFundsTypes: Array.isArray(
                data.sourceOfFundsWealth?.sourceOfFundsTypes
              )
                ? data.sourceOfFundsWealth.sourceOfFundsTypes
                : [],
            },
            pepSanctions: {
              ...prev.pepSanctions,
              ...normalizePepSanctionsForMobile(data.pepSanctions),
            },
            documents: { ...prev.documents, ...(data.documents || {}) },
            internalUse: { ...prev.internalUse, ...(data.internalUse || {}) },
          }));
        }
      } catch (e) {
        setLoadErr(e?.message || "Unexpected error while loading.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    run();
    return () => {
      alive = false;
    };
  }, [id, token]);

  function formWithLatestOwnershipGraph(sourceForm) {
    const nextForm =
      sourceForm && typeof sourceForm === "object" ? sourceForm : {};
    const ownership =
      nextForm.ownership && typeof nextForm.ownership === "object"
        ? nextForm.ownership
        : {};
    const accountHolderName =
      String(nextForm.customer?.name || "").trim() || "Account holder";
    const reviewOwnership =
      normalizeMobileOwnershipPercentsForReviewSnapshot(ownership);
    const liveOwnershipGraph = buildLiveReviewOwnershipSnapshot({
      ownership: reviewOwnership,
      accountHolderName,
    });

    if (!liveOwnershipGraph?.mermaid) return nextForm;

    return {
      ...nextForm,
      ownership: {
        ...ownership,
        assistantTree: liveOwnershipGraph,
      },
    };
  }

  async function apiSave(submit) {
    const formToSave = formWithLatestOwnershipGraph(form);
    const payload = {
      clientId: id,
      token,
      data: buildClientKycSaveData(formToSave, separateNames ? "separate" : "full"),
      embeddedDocuments: buildEmbeddedDocumentsPayload(),
      intakeFirstCompleted,
      submit: !!submit,
    };

    const { resp, json, rawText } = await fetchJsonSafe(
      `${API_BASE}/myKyc/clients-side/kyc/save`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    );

    if (!resp.ok || !json.ok) {
      throw new Error(json?.error || rawText || "Save failed");
    }
    return json;
  }

  async function handleSaveDraft() {
    try {
      if (saving) return;
      setErr("");
      setMsg("");
      setSaving(true);
      await apiSave(false);
      toastRef.current?.show({
        severity: "success",
        summary: "Draft saved",
        detail: "Your progress is safe.",
        life: 2500,
      });
    } catch (e) {
      setErr(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function doSubmitFinal() {
    try {
      if (saving) return;
      setErr("");
      setMsg("");
      setSaving(true);

      const now = new Date();

      if (!MOBILE_SUBMIT_PREVIEW_MODE) {
        await apiSave(true);

        window.localStorage.setItem(lsKey(id, token, "submitted"), "1");
        window.localStorage.setItem(
          lsKey(id, token, "submittedAt"),
          now.toISOString()
        );
        window.localStorage.setItem(
          lsKey(id, token, "clientName"),
          clientName || ""
        );
        window.localStorage.setItem(
          lsKey(id, token, "clientType"),
          clientType || ""
        );
      }

      setSubmitted(true);
      setSubmittedAtText(formatSubmittedAt(now));
    } catch (e) {
      setErr(e?.message || "Submit failed");
    } finally {
      setSaving(false);
    }
  }

  function handleSubmit() {
    if (saving) return;
    appConfirm({
      group: "appConfirm",
      header: "Confirm submission",
      message:
        "After submission, this link will stop working. Contact us if you need changes.",
      defaultFocus: "reject",
      acceptLabel: "Submit",
      rejectLabel: "Cancel",
      scheme: "beige",
      icon: "bi bi-send",
      accept: () => doSubmitFinal(),
    });
  }

  function showValidationToast(detail) {
    setToastBackdropVisible(true);
    window.setTimeout(() => {
      setToastBackdropVisible(false);
    }, 2600);

    toastRef.current?.show({
      severity: "secondary",
      summary: "A few details are still missing",
      detail,
      life: 2600,
    });
  }

  function openEmbeddedDocPicker(cardId) {
    if (saving || embeddedDocBusy) return;
    setEmbeddedDocActiveCardId(embeddedSafeStr(cardId));
    embeddedDocFileInputRef.current?.click?.();
  }

  useEffect(() => {
    if (embeddedAutoUploadRef.current) return;
    const hasQueued = embeddedDocItems.some((item) => item.status === "queued");
    if (!hasQueued) return;
    embeddedAutoUploadRef.current = true;
    uploadEmbeddedDocuments().finally(() => {
      embeddedAutoUploadRef.current = false;
    });
  }, [embeddedDocItems, embeddedDocBusy]); // eslint-disable-line

  function addEmbeddedDocFiles(fileList, cardId) {
    const cid = embeddedSafeStr(cardId);
    if (!cid) return;
    const files = Array.from(fileList || []);
    if (!files.length) return;

    setEmbeddedDocItems((prev) => [
      ...prev,
      ...files.map((file) => ({
        id: embeddedMakeId(),
        cardId: cid,
        file,
        name: file.name || "file",
        size: file.size || 0,
        type: file.type || "application/octet-stream",
        progress: 0,
        status: "queued",
        error: "",
        objectPath: "",
      })),
    ]);
  }

  function removeEmbeddedDocItem(itemId) {
    setEmbeddedDocItems((prev) => prev.filter((item) => item.id !== itemId));
  }

  async function ensureEmbeddedDocSession() {
    if (embeddedDocSession?.uploadPrefix) return embeddedDocSession;

    const { resp, json, rawText } = await fetchJsonSafe(
      `${API_BASE}/myKyc/clients-side/kyc/embedded-doc-upload/auth`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientId: id, token }),
      }
    );

    if (!resp.ok || json?.ok !== true) {
      throw new Error(json?.error || rawText || "Failed to start document upload.");
    }

    const firebaseToken = embeddedSafeStr(json.firebaseToken);
    if (!firebaseToken) throw new Error("Missing upload token.");

    try {
      await signOut(embeddedDocAuth);
    } catch {}
    const cred = await signInWithCustomToken(embeddedDocAuth, firebaseToken);
    await cred.user.getIdToken(true);

    const nextSession = {
      sessionId: embeddedSafeStr(json.sessionId),
      requestId: embeddedSafeStr(json.requestId),
      uploadPrefix: embeddedSafeStr(json.uploadPrefix),
      maxFileBytes: Number(json.maxFileBytes || 25 * 1024 * 1024),
    };
    setEmbeddedDocSession(nextSession);
    return nextSession;
  }

  async function uploadEmbeddedDocuments() {
    const queued = embeddedDocItems.filter((item) => item.status === "queued");
    if (!queued.length) return true;

    setEmbeddedDocBusy(true);
    setEmbeddedDocError("");

    try {
      const session = await ensureEmbeddedDocSession();
      const maxBytes = Number(session.maxFileBytes || 25 * 1024 * 1024);

      const uploadedCardIds = new Set();
      let hasFailedUpload = false;

      for (const item of queued) {
        if (!item.file) continue;
        if (Number(item.size || 0) > maxBytes) {
          hasFailedUpload = true;
          setEmbeddedDocItems((prev) =>
            prev.map((x) =>
              x.id === item.id
                ? { ...x, status: "error", error: "File is too large." }
                : x
            )
          );
          continue;
        }

        await new Promise((resolve) => {
          const objectPath = `${session.uploadPrefix}/${embeddedSafeStr(
            item.cardId || "card"
          )}/${Date.now()}_${sanitizeEmbeddedFilename(item.name)}`;
          const ref = storageRef(embeddedDocStorage, objectPath);

          setEmbeddedDocItems((prev) =>
            prev.map((x) =>
              x.id === item.id
                ? { ...x, status: "uploading", progress: 1, objectPath }
                : x
            )
          );

          const task = uploadBytesResumable(ref, item.file, {
            contentType: item.type,
          });

          task.on(
            "state_changed",
            (snap) => {
              const pct = snap.totalBytes
                ? Math.round((snap.bytesTransferred / snap.totalBytes) * 100)
                : 0;
              setEmbeddedDocItems((prev) =>
                prev.map((x) =>
                  x.id === item.id ? { ...x, progress: pct } : x
                )
              );
            },
            (error) => {
              hasFailedUpload = true;
              setEmbeddedDocItems((prev) =>
                prev.map((x) =>
                  x.id === item.id
                    ? {
                        ...x,
                        status: "error",
                        progress: 0,
                        error: error?.message || "Upload failed",
                      }
                    : x
                )
              );
              resolve();
            },
            () => {
              uploadedCardIds.add(embeddedSafeStr(item.cardId));
              setEmbeddedDocItems((prev) =>
                prev.map((x) =>
                  x.id === item.id
                    ? { ...x, status: "done", progress: 100 }
                    : x
                )
              );
              resolve();
            }
          );
        });
      }
      return { ok: !hasFailedUpload, uploadedCardIds };
    } catch (error) {
      const message = error?.message || "Document upload failed.";
      setEmbeddedDocError(message);
      showValidationToast(message);
      return { ok: false, uploadedCardIds: new Set() };
    } finally {
      setEmbeddedDocBusy(false);
    }
  }

  function buildEmbeddedDocumentsPayload() {
    if (!shouldRenderEmbeddedDocuments) return null;
    if (embeddedDocChoice === "later") {
      return { choice: "later" };
    }
    if (embeddedDocChoice !== "upload_now") return { choice: "" };

    const requirementByCard = new Map(
      embeddedDocRequirements.map((req) => [embeddedSafeStr(req.cardId), req])
    );
    const files = embeddedDocItems
      .filter((item) => item.status === "done" && item.objectPath)
      .map((item) => {
        const cardId = embeddedSafeStr(item.cardId);
        const req = requirementByCard.get(cardId);
        return {
          objectPath: item.objectPath,
          originalName: item.name,
          contentType: item.type,
          sizeBytes: item.size,
          cardId,
          cardTitle: embeddedSafeStr(req?.title) || null,
        };
      });

    return {
      choice: "upload_now",
      sessionId: embeddedDocSession?.sessionId || null,
      requestId: embeddedDocSession?.requestId || null,
      uploadPrefix: embeddedDocSession?.uploadPrefix || null,
      files,
    };
  }

  async function nextStep() {
    if (activeStep?.id === "customer" && !isCustomerStepComplete) {
      setCustomerContinueAttempted(true);
      showValidationToast("Complete all elements of the form to continue");
      return;
    }
    if (activeStep?.id === "ownership" && !isOwnershipStepComplete) {
      setOwnershipContinueAttempted(true);
      showValidationToast("Select one preliminary ownership option to continue");
      return;
    }
    if (activeStep?.id === "signatories" && !isSignatoriesStepComplete) {
      showValidationToast("Add at least one authorised signatory to continue");
      return;
    }
    if (activeStep?.id === "wealth" && !isWealthStepComplete) {
      setWealthContinueAttempted(true);
      showValidationToast(
        "Complete all required wealth and funding details to continue"
      );
      return;
    }
    if (activeStep?.id === "pepSanctions" && !isPepSanctionsStepComplete) {
      setPepSanctionsContinueAttempted(true);
      showValidationToast("Complete both declarations to continue");
      return;
    }
    if (activeStep?.id === "documents") {
      setEmbeddedDocAttempted(true);
      if (!embeddedDocChoice) {
        showValidationToast("Choose whether to upload documents now or provide them later");
        return;
      }
      if (embeddedDocChoice === "upload_now") {
        const latestDone = new Set(
          embeddedDocItems
            .filter((item) => item.status === "done" && item.objectPath)
            .map((item) => embeddedSafeStr(item.cardId))
        );
        if (embeddedQueuedCount > 0) {
          showValidationToast("Upload selected files before continuing");
          return;
        }
        const complete = embeddedDocRequirements.every((req) =>
          latestDone.has(embeddedSafeStr(req.cardId))
        );
        if (!complete) {
          showValidationToast("Upload one file for each requested document card to continue");
          return;
        }
      }
    }
    setStepIndex((v) => {
      const next = Math.min(v + 1, steps.length - 1);
      return steps[next]?.id === "initialDocuments" && intakeFirstCompleted ? next + 1 : next;
    });
  }

  function prevStep() {
    setStepIndex((v) => Math.max(v - 1, 0));
  }

  function renderStepBody() {
    switch (activeStep?.id) {
      case "intro":
        return (
          <div className="mobileKycIntroStack">
            <div className="mobileKycHeroCard">
              <div className="mobileKycHeroEyebrow">Confidential</div>
              <h1 className="mobileKycHeroTitle">A few quick steps</h1>
              <p className="mobileKycHeroText">
                This Know Your Customer (KYC) form is required for UAE
                regulatory and compliance purposes. Please complete it
                carefully and make sure the information is up to date.
              </p>
              {/* <p className="mobileKycHeroText">
                If you are unsure about a question, you can leave it blank and
                add a note in the comments section at the end.
              </p> */}
              <p className="mobileKycHeroText">
                You can save your progress at any time and continue later from
                the same secure link.
              </p>
              <p className="mobileKycHeroText mobileKycHeroText--hint">
                Your information will be kept confidential and used only for
                compliance purposes.
              </p>
            </div>

            {clientName ? (
              <div className="mobileKycClientRefCard">
                <div className="mobileKycClientRefLabel">Client</div>
                <div className="mobileKycClientRefValue">{clientName}</div>
              </div>
            ) : null}

            <div className="mobileKycChecklistCard">
              <div className="mobileKycChecklistTitle">What you’ll complete</div>
              {hasInitialDocumentsStep && <div className="mobileKycChecklistItem">Upload documents or skip</div>}
              <div className="mobileKycChecklistItem">Customer details</div>
              {isEntity ? (
                <>
                  <div className="mobileKycChecklistItem">Ownership and control</div>
                  <div className="mobileKycChecklistItem">Authorised signatories</div>
                </>
              ) : null}
              <div className="mobileKycChecklistItem">
                Source of wealth and funds
              </div>
              <div className="mobileKycChecklistItem">
                PEP and sanctions declaration
              </div>
              {shouldRenderEmbeddedDocuments ? (
                <div className="mobileKycChecklistItem">Documents</div>
              ) : null}
            </div>
          </div>
        );
      case "customer":
        return (
          <KycMobileCustomerDetails
            separateNames={separateNames}
            form={form}
            onSetAt={setAt}
            continueAttempted={customerContinueAttempted}
            useMainCountryOfOperation={useMainCountryOfOperation}
          />
        );
      case "ownership":
        return (
          <KycMobileOwnership
            entityTypeAnsweredEarlier={entityTypeAnsweredEarlier}
            separateNames={separateNames}
            form={form}
            onSetAt={setAt}
            readOnly={false}
            continueAttempted={ownershipContinueAttempted}
            view="ownership"
          />
        );
      case "signatories":
        return (
          <KycMobileOwnership
            entityTypeAnsweredEarlier={entityTypeAnsweredEarlier}
            separateNames={separateNames}
            form={form}
            onSetAt={setAt}
            readOnly={false}
            view="signatories"
          />
        );
      case "wealth":
        return (
          <KycMobileSowSof
            orgJurisdiction={orgJurisdiction}
            form={form}
            onSetAt={setAt}
            readOnly={false}
            continueAttempted={wealthContinueAttempted}
          />
        );
      case "pepSanctions":
        return (
          <KycMobilePepSanctions
            form={form}
            onSetAt={setAt}
            readOnly={false}
            continueAttempted={pepSanctionsContinueAttempted}
          />
        );
      case "documents":
        return (
          <div className="mobileKycEmbeddedDocs">
            <div className="kycMobileSegment">
              <button
                type="button"
                className={`kycMobileSegmentBtn ${embeddedDocChoice === "upload_now" ? "is-active" : ""}`}
                onClick={() => setEmbeddedDocChoice("upload_now")}
              >
                Upload now
              </button>
              <button
                type="button"
                className={`kycMobileSegmentBtn ${embeddedDocChoice === "later" ? "is-active" : ""}`}
                onClick={() => setEmbeddedDocChoice("later")}
              >
                Upload later
              </button>
            </div>

            <div className="kycEmbeddedDocsAmlNote">
              Additional supporting documents related to your profile may also be requested at a later stage as part of our standard compliance process, in line with AML regulations.
            </div>

            {embeddedDocAttempted && !embeddedDocChoice ? (
              <div className="kycMobileFieldError kycMobileFieldError--centered">
                <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />{" "}
                Choose one option to continue
              </div>
            ) : null}

            {embeddedDocError ? (
              <div className="alert alert-warning mobileKycAlert">
                {embeddedDocError}
              </div>
            ) : null}

            {embeddedDocChoice === "upload_now" ? (
              <div className="docUploadPartyList mobileKycEmbeddedDocsList">
                {embeddedGroupedRequirements.map((group) => {
                  const isParty =
                    group.kind === "individual" || group.kind === "entity";
                  const partyReq = embeddedDocRequirements.find(
                    (req) => req?.meta?.kind === "individual"
                  );
                  const firstReq = group.requirements?.[0] || null;
                  const headerName =
                    group.partyName ||
                    partyReq?.meta?.partyName ||
                    clientName ||
                    "Client";
                  const headerKind = isParty
                    ? getPartyPill(group.kind) || "Individual"
                    : "Individual";
                  const headerRole =
                    group.partyRole || partyReq?.meta?.partyRole || "Client";
                  const categoryLine = isParty
                    ? firstReq?.section || "Identification documents"
                    : `Source of Wealth • ${
                        firstReq?.title
                          ? stripMetaInTitle(firstReq.title)
                          : "Requirements"
                      }`;

                  return (
                    <div
                      key={group.key}
                      className={[
                        "docUploadPartyCard",
                        isParty ? "isParty" : "isSupportingDocs",
                        group.kind === "individual" ? "isIndividual" : "",
                        group.kind === "entity" ? "isEntity" : "",
                        "mobileKycEmbeddedDocsCard",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      <div className="docUploadPartyHeader">
                        <div className="docUploadPartyHeaderLeft">
                          <div className="docUploadPartyAvatar">
                            <img
                              src={getPartyIcon(group.kind)}
                              alt=""
                              aria-hidden="true"
                            />
                          </div>
                          <div className="docUploadPartyHeaderText">
                            <div className="docUploadPartyNameRow">
                              <div className="docUploadPartyName">
                                {headerName}
                              </div>
                            </div>
                            <div className="docUploadPartySub">
                              {categoryLine}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="docUploadPartyBody">
                        {group.requirements.map((req) => {
                          const cardId = embeddedSafeStr(req.cardId);
                          const cardItems = embeddedDocItems.filter(
                            (item) => embeddedSafeStr(item.cardId) === cardId
                          );
                          const lines = parseRichLines(req.description);

                          return (
                            <div key={cardId} className="docUploadThreeCardRow">
                              <div className="docUploadTwoCardsTop">
                                <div className="docUploadReqCard">
                                  <div className="docUploadReqBullets">
                                    {requirementLinesForDisplay(req, lines).map((line, idx) =>
                                      line.type === "heading" ? (
                                        <div
                                          key={`${cardId}_h_${idx}`}
                                          className="docUploadReqHeading"
                                        >
                                          {displayRequirementHeading(
                                            req,
                                            lines,
                                            line.text,
                                            line._displayIndex ?? idx
                                          )}
                                        </div>
                                      ) : (
                                        <div
                                          key={`${cardId}_b_${idx}`}
                                          className={
                                            line.type === "note"
                                              ? `docUploadReqNote${
                                                  isConditionalEntityRequirement(req, lines)
                                                    ? " docUploadReqNote--leftAligned"
                                                    : ""
                                                }`
                                              : "docUploadReqBullet"
                                          }
                                        >
                                          {line.type === "note" ? null : (
                                            <span className="docUploadReqBulletDot">•</span>
                                          )}
                                          <span className="docUploadReqBulletText">
                                            {line.type === "note"
                                              ? renderRequirementNoteText(req, lines, line.text)
                                              : line.text}
                                          </span>
                                          {getLineFlag({ req, line: line.text }) ? (
                                            <span className="docUploadFlag" title="Country">
                                              {getLineFlag({ req, line: line.text })}
                                            </span>
                                          ) : null}
                                        </div>
                                      )
                                    )}
                                  </div>
                                </div>

                                <div className="docUploadUploadCard">
                                  <div
                                    className={[
                                      "docUploadDropZone docUploadDropZoneStack",
                                      embeddedDocBusy ? "isDisabled" : "",
                                    ].join(" ")}
                                    onClick={() => openEmbeddedDocPicker(cardId)}
                                    role="button"
                                    tabIndex={0}
                                  >
                                    <div className="docUploadDropTopRow">
                                      <div className="docUploadDropIcon">
                                        <i className="bi bi-cloud-arrow-up" />
                                      </div>
                                      <div className="docUploadDropText">
                                        <div className="docUploadDropTitle">
                                          {cardItems.length ? "Add another file" : "Add file"}
                                        </div>
                                        <div className="docUploadDropSub">Tap to browse</div>
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      className="btn docUploadBtnOutline"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        openEmbeddedDocPicker(cardId);
                                      }}
                                      disabled={embeddedDocBusy}
                                    >
                                      Choose file
                                    </button>
                                  </div>
                                </div>
                              </div>

                              {cardItems.length > 0 ? (
                                <div className="docUploadFilesCard">
                                  <div className="docUploadFilesTitle">
                                    Uploaded documents
                                  </div>
                                  <div className="docUploadListWrap">
                                    {cardItems.map((item, idx) => (
                                      <div
                                        key={item.id}
                                        className={[
                                          "docUploadRow",
                                          idx === 0 ? "isFirst" : "",
                                          item.status === "error" ? "isError" : "",
                                        ].join(" ")}
                                      >
                                        <div className="docUploadRowIcon">
                                          {item.status === "done" ? (
                                            <i className="bi bi-check2" />
                                          ) : item.status === "error" ? (
                                            <i className="bi bi-exclamation-triangle-fill" />
                                          ) : item.status === "uploading" ? (
                                            <i className="bi bi-arrow-repeat" />
                                          ) : (
                                            <i className="bi bi-file-earmark" />
                                          )}
                                        </div>
                                        <div className="docUploadRowMain">
                                          <div className="docUploadRowName">
                                            {item.name}
                                            <span className="docUploadRowMetaInline">
                                              {" · "}{formatEmbeddedBytes(item.size)}
                                              {item.error ? ` · ${item.error}` : ""}
                                            </span>
                                          </div>
                                          {(item.status === "uploading" || item.status === "done") ? (
                                            <div className="docUploadProgress">
                                              <div
                                                className="docUploadProgressBar"
                                                style={{
                                                  width: `${Math.max(0, Math.min(100, item.progress || 0))}%`,
                                                }}
                                              />
                                            </div>
                                          ) : null}
                                        </div>
                                        {item.status !== "uploading" ? (
                                          <button
                                            type="button"
                                            className="btn docUploadIconBtn docUploadDoneTrashBtn"
                                            onClick={() => removeEmbeddedDocItem(item.id)}
                                            aria-label="Remove file"
                                          >
                                            <i className="bi bi-trash3" />
                                          </button>
                                        ) : null}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

              </div>
            ) : null}
          </div>
        );
      default:
        return (
          <div className="card kycCard mb-3">
            <div className="kycSectionBody">
              <textarea
                {...MOBILE_TEXT_ENTRY_PROPS}
                ref={commentTextareaRef}
                className="form-control mobileKycAutoGrowTextarea"
                rows={3}
                style={{
                  minHeight: COMMENT_MIN_HEIGHT,
                  maxHeight: COMMENT_MAX_HEIGHT,
                }}
                value={form?.clientComment || ""}
                onChange={(e) => {
                  autoResizeTextarea(e.currentTarget, {
                    minLines: 3,
                    maxLines: 20,
                  });
                  setAt(["clientComment"], e.target.value);
                }}
                onInput={(e) =>
                  autoResizeTextarea(e.currentTarget, {
                    minLines: 3,
                    maxLines: 20,
                  })
                }
              />
            </div>
          </div>
        );
    }
  }

  function renderMobilePreLanding() {
    return (
      <AnimatePresence>
        {showPreLanding && activeStep?.id === "intro" ? (
          <motion.div
            className="mobileKycPreLanding"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{
              opacity: 0,
              transition: { duration: 0.14, ease: [0.4, 0, 0.2, 1] },
            }}
          >
            <div className="mobileKycPreLandingLogo" aria-hidden="true">
              <img src={logoMyKycGrey} alt="" />
            </div>
            <div className="mobileKycPreLandingWordWrap">
              <div className="mobileKycPreLandingIntro">
                <span className="mobileKycPreLandingIntroText">
                  {PRE_LANDING_INTRO_WORDS.map((word, index) => (
                    <span
                      key={`${word}-${index}`}
                      className="mobileKycPreLandingIntroWord"
                      style={{ "--word-index": index }}
                    >
                      {word}
                    </span>
                  ))}
                </span>
              </div>
              <div
                className="mobileKycPreLandingWord"
                onAnimationEnd={handlePreLandingWordAnimationEnd}
              >
                <span className="mobileKycPreLandingWordToken">Know</span>
                <br />
                <span className="mobileKycPreLandingWordToken">Your</span>{" "}
                <span className="mobileKycPreLandingWordToken">Customer</span>
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    );
  }

  const copy = stepCopy(activeStep?.id, clientName, isEntity, entityDocumentsTitle(documentsRequest));
  const displayEyebrow =
    activeStep?.id === "intro"
      ? copy.eyebrow
      : `Step ${progressSteps.findIndex((step) => step.id === activeStep?.id) + 1}`;

  if (loadErr) {
    return (
      <motion.div
        variants={pageAnim}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.25 }}
        className="KycClientSideFormMobilePage KycClientSideFormMobilePage--linkError"
      >
        {renderMobilePreLanding()}

        <div className="mobileKycShell">
          <div className="mobileKycLinkErrorContent">
            <motion.div
              className="mobileKycLinkErrorHeader"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="mobileKycLinkErrorBadge">Secure Form · Link expired</div>

              <Lottie
                animationData={timerExpAnim}
                loop={true}
                lottieRef={(ref) => ref?.setSpeed(0.5)}
                className="mobileKycLinkErrorLottie"
                aria-hidden="true"
              />

              <motion.h1
                className="mobileKycLinkErrorHeadline"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.38, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                This link can't be used
              </motion.h1>

              <motion.p
                className="mobileKycLinkErrorLead"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.38, delay: 0.22, ease: [0.22, 1, 0.36, 1] }}
              >
                {loadErr}
              </motion.p>
            </motion.div>

            <motion.div
              className="mobileKycLinkErrorNextCard"
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.42, delay: 0.28, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="mobileKycLinkErrorNextTitle">What to do next</div>
              <div className="mobileKycLinkErrorNextItem">
                <span className="mobileKycLinkErrorNextDot" aria-hidden="true" />
                <span>Please request a new secure link from the staff team.</span>
              </div>
              <div className="mobileKycLinkErrorNextItem">
                <span className="mobileKycLinkErrorNextDot" aria-hidden="true" />
                <span>If you believe this is an error, you can try refreshing the page.</span>
              </div>
              <div className="mobileKycLinkErrorActions">
                <button
                  type="button"
                  className="btn mobileKycGhostBtn"
                  onClick={() => window.location.reload()}
                >
                  Retry
                </button>
              </div>
            </motion.div>
          </div>

          <motion.div
            className="kycSentFooterBrand"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5, ease: "easeInOut" }}
          >
            <img src={logoMyKycGrey} alt="myKYC" className="kycSentFooterLogo kycSentFooterLogoError" />
          </motion.div>
        </div>
      </motion.div>
    );
  }

  if (submitted) {
    return (
      <motion.div
        variants={pageAnim}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.25 }}
        className="KycClientSideFormMobilePage KycClientSideFormMobilePage--submitted"
      >
        {renderMobilePreLanding()}
        <div className="mobileKycShell">
          <motion.div
            className="mobileKycSuccessHeader"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="mobileKycSuccessBadge">Submission received</div>

            <div className="mobileKycSuccessOrb" aria-hidden="true">
              <motion.div
                className="mobileKycSuccessOrbPulse"
                initial={{ scale: 0.78, opacity: 0.2 }}
                animate={{ scale: 1.16, opacity: 0.52 }}
                transition={{
                  duration: 1.6,
                  repeat: Infinity,
                  repeatType: "mirror",
                  ease: "easeInOut",
                }}
              />
              <motion.div
                className="mobileKycSuccessOrbCore"
                initial={{ scale: 0.7, rotate: -16, opacity: 0 }}
                animate={{ scale: 1, rotate: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                <i className="bi bi-check2" />
              </motion.div>
            </div>

            <motion.h1
              className="mobileKycSuccessHeadline"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.38, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
            >
              KYC information received
            </motion.h1>

            {clientName ? (
              <motion.div
                className="mobileKycSuccessClientBadge"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.38, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
              >
                {clientName}
              </motion.div>
            ) : null}

            <motion.p
              className="mobileKycSuccessLead"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.38, delay: 0.22, ease: [0.22, 1, 0.36, 1] }}
            >
              Your submission has been securely delivered to our team.
            </motion.p>

            {submittedAtText ? (
              <motion.div
                className="mobileKycSuccessTimestamp"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.34, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              >
                Submitted on {submittedAtText}
              </motion.div>
            ) : null}
          </motion.div>

          <motion.div
            className="mobileKycSuccessNextCard"
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.42, delay: 0.24, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="mobileKycSuccessNextTitle">What happens next</div>
            <div className="mobileKycSuccessNextItem">
              <span className="mobileKycSuccessNextDot" aria-hidden="true" />
              <span>
                Our Compliance team will review the submission and revert if
                necessary.
              </span>
            </div>
            <div className="mobileKycSuccessNextItem">
              <span className="mobileKycSuccessNextDot" aria-hidden="true" />
              <span>
                You may also be asked to provide supporting documentation to
                complete the review.
              </span>
            </div>
          </motion.div>

          <motion.div
            className="kycSentFooterBrand"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5, ease: "easeInOut" }}
          >
            <img src={logoMyKycLightblue3} alt="myKYC" className="kycSentFooterLogo kycSentFooterLogoSubmit" />
          </motion.div>
        </div>
      </motion.div>
    );
  }

  const showInitialDocumentsCard = activeStep?.id === "initialDocuments" && !loading && !loadErr && !submitted && !showPreLanding &&
    documentsTiming === "start";

  return (
    <OwnershipLabelsProvider jurisdiction={orgJurisdiction}>
    <motion.div
      variants={activeStep?.id === "intro" ? steadyPageAnim : pageAnim}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={
        activeStep?.id === "intro" ? { duration: 0 } : { duration: 0.25 }
      }
      className="KycClientSideFormMobilePage"
    >
      {toastBackdropVisible ? <div className="mobileKycToastBackdrop" /> : null}
      {renderMobilePreLanding()}


      <div className="mobileKycShell" ref={topRef}>
        <input
          ref={embeddedDocFileInputRef}
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={(event) => {
            addEmbeddedDocFiles(event.target.files, embeddedDocActiveCardId);
            event.target.value = "";
          }}
        />

        {showProgressHeader ? (
          <div className="mobileKycProgressCard" inert={showInitialDocumentsCard}>
            <div className="mobileKycProgressMeta">
              <span>
                Step {progressStepIndex + 1} of {progressSteps.length}
              </span>
            </div>
            <div className="mobileKycDotRow">
              {progressSteps.map((step, idx) => {
                const actualIdx = steps.findIndex((s) => s.id === step.id);
                return (
                  <button
                    key={step.id}
                    type="button"
                    className={`mobileKycDot ${
                      idx === progressStepIndex
                        ? "is-active"
                        : idx < progressStepIndex
                        ? "is-complete"
                        : ""
                    }`}
                    onClick={() => { if (actualIdx >= 0) setStepIndex(actualIdx); }}
                    disabled={step.id === "initialDocuments" && intakeFirstCompleted}
                    aria-current={idx === progressStepIndex ? "step" : undefined}
                    aria-label={step.id === "initialDocuments" ? `Upload documents${intakeFirstCompleted ? " completed" : ""}` : `Go to ${step.label}`}
                  />
                );
              })}
            </div>
          </div>
        ) : null}

        {err ? <div className="alert alert-danger mobileKycAlert">{err}</div> : null}
        {msg ? <div className="alert alert-success mobileKycAlert">{msg}</div> : null}

      <AnimatePresence initial={false} mode="wait">
        {showInitialDocumentsCard && (
          <motion.div key={id + token} initial={false}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0, pointerEvents: "none" }}
            transition={{
              height: { duration: reduceIntakeMotion ? 0 : 0.6, ease: [0.22, 1, 0.36, 1] },
              opacity: { duration: reduceIntakeMotion ? 0 : 0.3 },
            }}
            style={{ overflow: "hidden" }}>
            <KycInitialDocumentsModal inline mobile stepLabel="Step 1" clientId={id} token={token}
              form={form} separateNames={separateNames} setForm={setForm}
              includeCv={!isEntity && documentsRequest.individualsCv}
              entity={isEntity} corporateDocuments={corporateDocuments} entityDocTypes={documentsRequest.entitiesDocTypes}
              onComplete={() => {
                setIntakeFirstCompleted(true);
                setStepIndex(steps.findIndex(step => step.id === "customer"));
              }} />
          </motion.div>
        )}
        {activeStep?.id !== "initialDocuments" && (
        <motion.div key="step-content" className="kycInitialDocsFollowing"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ duration: reduceIntakeMotion ? 0 : 0.2 }}>
        <div
          className={`mobileKycStageCard ${
            activeStep?.id === "intro" ? "mobileKycStageCard--intro" : ""
          }`}
        >
          {activeStep?.id === "intro" ? (
            <motion.div
              className="mobileKycStageBrand"
              style={{ userSelect: "none" }}
              initial={{ opacity: 0, y: -22, scaleY: 0.92 }}
              animate={{ opacity: 1, y: 0, scaleY: 1 }}
              transition={{
                delay: 0.12,
                duration: 0.56,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              <div className="mobileKycIntroLogoGlow" aria-hidden="true" />
              <img
                src={logoMyKycGrey}
                alt="myKYC"
                className="mobileKycIntroLogo"
              />
            </motion.div>
          ) : null}
          {activeStep?.id === "intro" ? (
            <motion.div variants={introContentFadeAnim} initial="initial" animate="animate" style={{ userSelect: "none" }}>
              <div className="mobileKycStageEyebrow">
                {displayEyebrow}
              </div>
              <div className="mobileKycStageTitle">
                {copy.title}
              </div>
              <div className="mobileKycStageHint">
                {copy.hint}
              </div>

              <div className="mobileKycStageBody">{renderStepBody()}</div>
            </motion.div>
          ) : (
            <>
              <div className="mobileKycStageEyebrow">{displayEyebrow}</div>
              <div className="mobileKycStageTitle">{copy.title}</div>
              <div className="mobileKycStageHint">{copy.hint}</div>
            </>
          )}

          {activeStep?.id === "intro" ? null : (
            <div className="mobileKycStageBody">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeStep?.id}
                  variants={stepBodyFadeAnim}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  {renderStepBody()}
                </motion.div>
              </AnimatePresence>
            </div>
          )}
        </div>

        {activeStep?.id === "customer" && !isCustomerStepComplete ? (
          <div className="mobileKycBottomHint">
            Complete the required customer details to continue.
          </div>
        ) : null}

        <div className="mobileKycBottomBar">
          {activeStep?.id === "intro" ? (
            <button
              type="button"
              className="btn mobileKycPrimaryBtn"
              onClick={nextStep}
              disabled={saving}
            >
              Start KYC
            </button>
          ) : (
            <>
              <button
                type="button"
                className="btn mobileKycIconBtn"
                onClick={prevStep}
                disabled={saving}
                aria-label="Back"
                title="Back"
              >
                <i className="bi bi-chevron-left" aria-hidden="true" />
              </button>

              <button
                type="button"
                className="btn mobileKycGhostBtn"
                onClick={handleSaveDraft}
                disabled={saving}
              >
                Save draft
              </button>

              {activeStep?.id === "comments" ? (
                <button
                  type="button"
                  className="btn mobileKycPrimaryBtn"
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving ? "Submitting…" : "Submit"}
                </button>
              ) : (
                <button
                  type="button"
                  className={`btn mobileKycPrimaryBtn ${
                    showBlockedPepContinue ||
                    showBlockedDocumentsContinue ||
                    (activeStep?.id === "customer" && !isCustomerStepComplete) ||
                    (activeStep?.id === "wealth" && !isWealthStepComplete)
                      ? "mobileKycPrimaryBtn--blocked"
                      : ""
                  }`}
                  onClick={nextStep}
                  disabled={
                    activeStep?.id === "documents" && embeddedDocBusy
                      ? true
                      : activeStep?.id === "customer"
                      ? saving
                      : activeStep?.id === "wealth"
                      ? saving
                      : showBlockedPepContinue || showBlockedDocumentsContinue
                        ? false
                        : isContinueDisabled
                  }
                  aria-disabled={
                    showBlockedPepContinue ||
                    showBlockedDocumentsContinue ||
                    (activeStep?.id === "customer" && !isCustomerStepComplete) ||
                    (activeStep?.id === "wealth" && !isWealthStepComplete)
                      ? "true"
                      : undefined
                  }
                >
                  Continue
                </button>
              )}
            </>
          )}
        </div>
        </motion.div>
        )}
      </AnimatePresence>

      </div>

      <Toast ref={toastRef} position="top-center" className="mobileKycToast" />
    </motion.div>
    </OwnershipLabelsProvider>
  );
}
